import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { rccColors } from "../colors";
import { useRccTypography } from "../typography";

const FONT_FAMILY = '"Onest", sans-serif';

/**
 * D3GroupedBar - Configuration-driven grouped bar chart component
 * Displays multiple series as grouped bars for each category.
 *
 * @param {Object} props
 * @param {Array} props.data - Chart data array with shape:
 *   [{ quarter: string, [series1]: number, [series2]: number, ... }]
 * @param {Array} props.series - Array of series keys to display (e.g., ['winRateByOppCount', 'winRateByACV'])
 * @param {Object} props.colorPalette - Map of series key -> color hex code
 *   e.g. { 'winRateByOppCount': '#42A5F5', 'winRateByACV': '#1976D2' }
 * @param {Object} props.options - Optional configuration
 * @param {Function} props.options.yAxisFormatter - Function to format y-axis values (default: formats as percentage)
 * @param {Function} props.options.valueFormatter - Function to format values above bars (default: formats as percentage)
 * @param {Function} props.options.xAxisFormatter - Function to format x-axis labels (default: identity)
 * @param {boolean} props.options.showValueLabels - Show values above bars (default: true)
 * @param {boolean} props.options.showLegend - Show legend (default: true)
 * @param {Object} props.options.legendLabels - Map of series key -> legend label text
 * @param {boolean} props.options.showGridlines - Show horizontal gridlines (default: true)
 * @param {number} props.options.marginTop - Top margin (default: 20)
 * @param {number} props.options.marginRight - Right margin (default: 30)
 * @param {number} props.options.marginBottom - Bottom margin (default: 50)
 * @param {number} props.options.marginLeft - Left margin (default: 55)
 * @param {number} props.options.groupPadding - Padding between groups (default: 0.3)
 * @param {number} props.options.barPadding - Padding between bars in a group (default: 0.1)
 * @param {number} props.options.yAxisMax - Maximum Y-axis value (default: auto-calculated)
 */
function D3GroupedBar({ data, series, colorPalette, options = {} }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const lastDimensions = useRef({ width: 0, height: 0 });
  const typography = useRccTypography();

  const {
    yAxisFormatter = (v) => `${v}%`,
    valueFormatter = (v) => `${v}%`,
    xAxisFormatter = (v) => v,
    showValueLabels = true,
    showLegend = true,
    legendLabels = {},
    showGridlines = true,
    marginTop = 20,
    marginRight = 30,
    marginBottom = 50,
    marginLeft = 55,
    groupPadding = 0.3,
    barPadding = 0.1,
    yAxisMax,
  } = options;

  useEffect(() => {
    if (
      !wrapperRef.current ||
      !containerRef.current ||
      !data?.length ||
      !series?.length
    )
      return;

    const renderChart = () => {
      const wrapper = wrapperRef.current;
      const container = containerRef.current;
      if (!wrapper || !container) return;

      const containerWidth = wrapper.clientWidth;
      const containerHeight = wrapper.clientHeight;

      const width = containerWidth - marginLeft - marginRight;
      const height = containerHeight - marginTop - marginBottom;

      if (width <= 0 || height <= 0) return;

      // Skip if dimensions haven't changed (prevents resize loop)
      const { width: lastW, height: lastH } = lastDimensions.current;
      if (containerWidth === lastW && containerHeight === lastH) return;

      // Store current dimensions before drawing
      lastDimensions.current = {
        width: containerWidth,
        height: containerHeight,
      };

      // Clear SVG
      d3.select(container).selectAll("*").remove();

      const svg = d3
        .select(container)
        .append("svg")
        .attr("width", containerWidth)
        .attr("height", containerHeight)
        .append("g")
        .attr("transform", `translate(${marginLeft},${marginTop})`);

      // Prepare data - filter out invalid entries
      const validData = data.filter((d) => {
        const quarter = d.quarter || d.xAxisLabel;
        return (
          quarter && series.some((s) => d[s] !== undefined && d[s] !== null)
        );
      });

      if (validData.length === 0) return;

      // Scales
      const x0 = d3
        .scaleBand()
        .domain(validData.map((d) => d.quarter || d.xAxisLabel))
        .range([0, width])
        .padding(groupPadding);

      const x1 = d3
        .scaleBand()
        .domain(series)
        .range([0, x0.bandwidth()])
        .padding(barPadding);

      // Calculate max value
      const maxValue =
        yAxisMax !== undefined
          ? yAxisMax
          : d3.max(validData, (d) => d3.max(series.map((s) => d[s] || 0))) *
            1.1;

      const yScale = d3
        .scaleLinear()
        .domain([0, maxValue])
        .nice()
        .range([height, 0]);

      // Gridlines (if enabled)
      if (showGridlines) {
        const yAxisGrid = d3
          .axisLeft(yScale)
          .ticks(10)
          .tickSize(-width)
          .tickFormat("");
        const gridGroup = svg.append("g").attr("class", "grid");

        gridGroup.call(yAxisGrid);
        gridGroup.select(".domain").remove();
        gridGroup
          .selectAll("line")
          .attr("stroke", rccColors.neutral[200])
          .attr("stroke-dasharray", "0");
      }

      // Y-Axis
      const yAxis = d3.axisLeft(yScale).ticks(10).tickFormat(yAxisFormatter);
      const yAxisGroup = svg.append("g").call(yAxis);

      yAxisGroup
        .select(".domain")
        .attr("stroke", rccColors.neutral[100])
        .attr("stroke-width", 1);
      yAxisGroup.selectAll(".tick line").attr("stroke", rccColors.neutral[300]);
      yAxisGroup
        .selectAll(".tick text")
        .attr("class", typography.textExtraSmallRegular)
        .style("fill", rccColors.neutral[800])
        .style("font-family", FONT_FAMILY);

      // X-Axis
      const xAxis = d3
        .axisBottom(x0)
        .tickFormat(xAxisFormatter)
        .tickSizeInner(6)
        .tickSizeOuter(0);
      const xAxisGroup = svg
        .append("g")
        .attr("transform", `translate(0,${height})`)
        .call(xAxis);

      xAxisGroup.select(".domain").attr("stroke", rccColors.neutral[200]);
      xAxisGroup.selectAll(".tick line").attr("stroke", rccColors.neutral[200]);
      xAxisGroup
        .selectAll(".tick text")
        .attr("class", typography.textExtraSmallRegular)
        .style("fill", rccColors.neutral[800])
        .style("font-family", FONT_FAMILY);

      // Draw bars
      validData.forEach((d) => {
        const group = svg
          .append("g")
          .attr("transform", `translate(${x0(d.quarter || d.xAxisLabel)},0)`);

        series.forEach((seriesKey) => {
          const value = d[seriesKey];
          if (value === undefined || value === null || isNaN(value)) return;

          const barX = x1(seriesKey);
          const barWidth = x1.bandwidth();
          const barY = yScale(value);
          const barHeight = height - barY;

          const color = colorPalette[seriesKey] || rccColors.neutral[400];

          // Draw bar when height is positive
          if (barHeight > 0) {
            group
              .append("rect")
              .attr("x", barX)
              .attr("y", barY)
              .attr("width", barWidth)
              .attr("height", barHeight)
              .attr("fill", color);
          }

          if (showValueLabels) {
            const baseY = value === 0 ? height : barY;
            const labelY = baseY - 8;
            group
              .append("text")
              .attr("x", barX + barWidth / 2)
              .attr("y", labelY)
              .attr("text-anchor", "middle")
              .attr("class", typography.textExtraSmallSemibold)
              .style("fill", rccColors.neutral[800])
              .style("font-family", FONT_FAMILY)
              .text(valueFormatter(value));
          }
        });
      });
    };

    renderChart();

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(renderChart, 50);
    });
    resizeObserver.observe(wrapperRef.current);

    return () => resizeObserver.disconnect();
  }, [
    data,
    series,
    colorPalette,
    options,
    yAxisFormatter,
    valueFormatter,
    xAxisFormatter,
    showValueLabels,
    showGridlines,
    marginLeft,
    marginTop,
    marginBottom,
    marginRight,
    groupPadding,
    barPadding,
    yAxisMax,
    typography,
  ]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        ref={wrapperRef}
        style={{
          width: "100%",
          flex: 1,
          position: "relative",
          minHeight: 0,
        }}
      >
        <div
          ref={containerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            margin: 0,
            padding: 0,
          }}
        />
      </div>
      {showLegend && series.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: "16px",
            gap: "48px",
            flexWrap: "wrap",
          }}
        >
          {series.map((seriesKey) => {
            const label = legendLabels[seriesKey] || seriesKey;
            const color = colorPalette[seriesKey] || rccColors.neutral[400];
            return (
              <div
                key={seriesKey}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <span
                  className={typography.textSmallRegular}
                  style={{ color: rccColors.neutral[800] }}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default D3GroupedBar;
